import axios from 'axios';
import { SAFEA_V3_ENABLED, TOKEN_REFRESH_ERRORS } from '~/constants/_core/safea-v3';
import { getImpersonateData } from '~/helpers/_core/impersonate';
import { buildDefaultAuthorizeUrl } from '~/helpers/_core/safea-v3';

export default function(app, options = { locale: null, token: null }) {
  return {
    withToken(headers = {}, customBaseURL = null) {
      const config = {
        baseURL : customBaseURL ?? process.env.SAFEA_API,
        headers : {
          'Accept'          : 'application/json',
          'Content-Type'    : 'application/json',
          'Accept-Language' : app ? app.i18n.locale : options.locale,
          ...headers
        }
      };

      const token = app ? app.$token.get() : options.token;
      if (token) {
        config.headers['Authorization'] = token;
      }

      if (app) {
        return app.$axios.create(config);
      }

      return axios.create(config);
    },
    default(headers = {}) {
      const config = {
        baseURL : process.env.SAFEA_API,
        headers : {
          'Accept'          : 'application/json',
          'Content-Type'    : 'application/json',
          'Accept-Language' : app ? app.i18n.locale : options.locale,
          ...headers
        }
      };
      if (app) {
        return app.$axios.create(config);
      }

      return axios.create(config);
    }
  };
}

// Stall the response so the user doesn't see an error
function stallResponse() {
  return new Promise(resolve => setTimeout(resolve, 2000));
}

export async function unauthorizedInterceptor(app, error) {
  const originalRequest = error.config;
  if (error?.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    if (!app.$token.hasTokens()) {
      const authorizeUrl = await buildDefaultAuthorizeUrl({
        store : app.store,
        route : app.router.currentRoute
      });

      window.location.assign(authorizeUrl);
      await stallResponse();
      throw error;
    }

    try {
      const { refreshToken } = app.$token.getTokens();

      let tokens;
      if (app.store.getters['_core/auth/isImpersonated']) {
        // If the user is impersonated, we need to refresh the token with the impersonated user
        const impersonate = getImpersonateData({ store: app.store });
        tokens = await app.$services._core.authV3().impersonate({
          refreshToken,
          username  : impersonate.username,
          userId    : impersonate.userId,
          roleAlias : impersonate.roleAlias
        });
      } else {
        tokens = await app.$services._core.oauthV3().refreshToken({ refreshToken });
      }

      const accessToken = `${tokens.token_type} ${tokens.access_token}`;
      await app.$token.setTokens({
        idToken      : tokens.id_token,
        refreshToken : tokens.refresh_token,
        accessToken
      });

      return axios({
        ...originalRequest,
        headers: {
          ...originalRequest.headers,
          Authorization: accessToken
        }
      });
    } catch (err) {
      app.$token.clear();

      // The refresh token is probably expired, just authorize again
      const errorCode = err?.response?.data?.error;
      if (TOKEN_REFRESH_ERRORS.includes(errorCode)) {
        const authorizeUrl = await buildDefaultAuthorizeUrl({
          store : app.store,
          route : app.router.currentRoute
        });

        window.location.assign(authorizeUrl);
        await stallResponse();
      }

      throw err;
    }
  }
  // Not 401, just pass the error
  throw error;
}

async function unauthorizedInterceptorWebComponent({ wcStore, wcServices }, error) {
  const originalRequest = error.config;

  if (error?.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    const tokens = await wcServices._core.oauthV3().refreshToken({
      refreshToken: wcStore.state['_core/auth']['refreshToken']
    });

    const accessToken = `${tokens.token_type} ${tokens.access_token}`;
    wcStore.commit('_core/auth/SET_TOKENS', {
      idToken      : tokens.id_token,
      refreshToken : tokens.refresh_token,
      accessToken
    });

    return axios({
      ...originalRequest,
      headers: {
        ...originalRequest.headers,
        Authorization: accessToken
      }
    });
  }

  // Not 401, just pass the error
  throw error;
}

function authorizationHeaderInterceptor({ app, wcStore }, config) {
  function getAuthToken() {
    if (!app) {
      return wcStore.state['_core/auth']['accessToken'];
    }

    if (!app.$token.hasTokens()) {
      return null;
    }

    return app.$token.getTokens().accessToken;
  }

  const authToken = getAuthToken();
  if (authToken) {
    config.headers['Authorization'] = authToken;
  }

  return config;
}

export function axiosV3(app, options = { baseURL: null, accessToken: null, refreshToken: null, locale: null, wcStore: null, wcServices: null }) {
  function createAxios(headers = {}) {
    return axios.create({
      baseURL : options.baseURL ?? process.env.SAFEA_API_V3,
      headers : {
        'Accept'          : 'application/json',
        'Content-Type'    : 'application/json',
        'Accept-Language' : app ? app.i18n.locale : options.locale,
        ...headers
      }
    });
  }

  return {
    withToken(headers = {}) {
      if (!SAFEA_V3_ENABLED) {
        throw new Error('[Cockpit] Why are you using Safe A v3 if you are still on v2?');
      }

      const axiosInstance = createAxios({
        ...headers
      });

      axiosInstance.interceptors.request.use(
        (config) => authorizationHeaderInterceptor({ app, wcStore: options.wcStore }, config)
      );

      if (!app) {
        axiosInstance.interceptors.response.use(
          response => response,
          unauthorizedInterceptorWebComponent.bind(null, options)
        );
      } else {
        axiosInstance.interceptors.response.use(
          response => response,
          unauthorizedInterceptor.bind(null, app)
        );
      }

      return axiosInstance;
    },
    default(headers = {}) {
      if (!SAFEA_V3_ENABLED) {
        throw new Error('[Cockpit] Why are you using Safe A v3 if you are still on v2?');
      }

      return createAxios(headers);
    }
  };
}
